import { ReactNode, startTransition, useEffect, useRef, useState } from "react";

interface DeferredRenderProps {
  children: ReactNode;
  fallback?: ReactNode | null;
  timeout?: number;
}

const DEFAULT_TIMEOUT = 300;

/**
 * A component that uses startTransition to defer rendering of its children for non-critical content.
 * A timeout is used as a safety net to ensure that the children are rendered after a certain amount of time.
 * @param children - The children to render.
 * @param fallback - The fallback to render while the children are not rendered.
 * @param timeout - The timeout in milliseconds to render the children. Defaults to 300ms.
 * @returns The children or the fallback.
 */

export const DeferredRender = ({
  children,
  fallback = null,
  timeout = DEFAULT_TIMEOUT,
}: DeferredRenderProps): JSX.Element => {
  const [shouldRender, setShouldRender] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    startTransition(() => {
      setShouldRender(true);
    });

    // timeout safety net
    timeoutRef.current = setTimeout(() => {
      setShouldRender(true);
    }, timeout);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeout]);

  // Clean up timeout when shouldRender becomes true
  useEffect(() => {
    if (shouldRender && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [shouldRender]);

  const content = shouldRender ? children : fallback;

  return <>{content}</>;
};
